// src/components/Hero.js
import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaGithub, FaLinkedin, FaTwitter, FaFacebook, FaWhatsapp } from "react-icons/fa";
import { useSite } from "../context/SiteContext";
import "./Hero.css";

const ICON_MAP = { FaGithub, FaLinkedin, FaTwitter, FaFacebook, FaWhatsapp };

// ── Typewriter hook ─────────────────────────────────────────────────────────
// Uses refs for internal state so the effect never re-fires due to
// re-renders caused by the parent component. The words array is consumed
// once on mount via a ref so an inline array definition won't cause restarts.
const useTypewriter = (words, { typingSpeed = 80, deletingSpeed = 42, pauseMs = 2000 } = {}) => {
  const [displayed, setDisplayed] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  // Keep stable refs so the interval callback always sees current values
  const wordsRef     = useRef(words);
  const indexRef     = useRef(0);
  const deletingRef  = useRef(false);
  const displayedRef = useRef("");

  // Sync words ref if prop changes
  useEffect(() => { wordsRef.current = words; }, [words]);

  useEffect(() => {
    let timer;

    const tick = () => {
      const wordList = wordsRef.current;
      if (!wordList?.length) return;
      const word = wordList[indexRef.current % wordList.length];

      if (!deletingRef.current) {
        // Typing
        if (displayedRef.current.length < word.length) {
          displayedRef.current = word.slice(0, displayedRef.current.length + 1);
          setDisplayed(displayedRef.current);
          timer = setTimeout(tick, typingSpeed);
        } else {
          // Finished typing — pause then start deleting
          timer = setTimeout(() => {
            deletingRef.current = true;
            setIsDeleting(true);
            tick();
          }, pauseMs);
        }
      } else {
        // Deleting
        if (displayedRef.current.length > 0) {
          displayedRef.current = displayedRef.current.slice(0, -1);
          setDisplayed(displayedRef.current);
          timer = setTimeout(tick, deletingSpeed);
        } else {
          // Finished deleting — advance to next word
          indexRef.current = (indexRef.current + 1) % wordList.length;
          deletingRef.current = false;
          setIsDeleting(false);
          timer = setTimeout(tick, typingSpeed);
        }
      }
    };

    timer = setTimeout(tick, typingSpeed);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — runs once on mount only

  return { displayed, isDeleting };
};

// ── Live clock hook ─────────────────────────────────────────────────────────
const useClock = (timezone = "Africa/Nairobi") => {
  const [time, setTime] = useState("");
  useEffect(() => {
    const fmt = () =>
      new Date().toLocaleTimeString("en-GB", {
        timeZone: timezone,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    setTime(fmt());
    const id = setInterval(() => setTime(fmt()), 1000);
    return () => clearInterval(id);
  }, [timezone]);
  return time;
};

// ── Particle canvas ─────────────────────────────────────────────────────────
const ParticleField = () => {
  const ref = useRef(null);
  const mouse = useRef({ x: -9999, y: -9999 });

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();

    const onResize = () => resize();
    const onMove   = (e) => { mouse.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener("resize", onResize);
    window.addEventListener("mousemove", onMove, { passive: true });

    const N = 50;
    const pts = Array.from({ length: N }, () => ({
      x:  Math.random() * canvas.width,
      y:  Math.random() * canvas.height,
      r:  Math.random() * 1.4 + 0.4,
      vx: (Math.random() - 0.5) * 0.22,
      vy: (Math.random() - 0.5) * 0.22,
      o:  Math.random() * 0.35 + 0.08,
    }));

    const tick = () => {
      const { width: W, height: H } = canvas;
      ctx.clearRect(0, 0, W, H);
      const { x: mx, y: my } = mouse.current;

      for (const p of pts) {
        const dx = p.x - mx, dy = p.y - my;
        const d  = Math.hypot(dx, dy);
        if (d < 110 && d > 0) {
          const f = ((110 - d) / 110) * 0.05;
          p.vx += (dx / d) * f;
          p.vy += (dy / d) * f;
        }
        p.vx *= 0.97; p.vy *= 0.97;
        p.x  += p.vx;  p.y  += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(232,108,47,${p.o})`;
        ctx.fill();
      }

      for (let i = 0; i < N; i++) {
        for (let j = i + 1; j < N; j++) {
          const dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y;
          const d  = Math.hypot(dx, dy);
          if (d < 95) {
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = `rgba(232,108,47,${0.07 * (1 - d / 95)})`;
            ctx.lineWidth   = 0.5;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  return <canvas ref={ref} className="hero__particles" aria-hidden="true" />;
};

// ── Main ────────────────────────────────────────────────────────────────────
const Hero = () => {
  const { profile, socials } = useSite();
  const gridRef = useRef(null);

  const roles = useMemo(() => [
    profile.title     || "Full-Stack Developer",
    "Open Source Advocate",
    "Full-Stack Engineer",
    "Systems Builder",
    "Web & Mobile Developer",
    "Impactful Software Creator",
    "Tech Enthusiast",
    "Lifelong Learner",
  ], [profile.title]);

  const { displayed, isDeleting } = useTypewriter(roles, {
    typingSpeed:   95,   // ms per character typed   (was 80)
    deletingSpeed: 55,   // ms per character deleted  (was 42)
    pauseMs:       3200, // ms to hold the full word  (was 2000)
  });
  const clock = useClock("Africa/Nairobi");

  useEffect(() => {
    const el = gridRef.current;
    const onMove = (e) => {
      if (!el) return;
      const x = (e.clientX / window.innerWidth  - 0.5) * 10;
      const y = (e.clientY / window.innerHeight - 0.5) * 10;
      el.style.transform = `translate(${x}px, ${y}px)`;
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <section className="hero">
      <div className="hero__grid" ref={gridRef} aria-hidden="true" />
      <div className="hero__orb hero__orb--1" aria-hidden="true" />
      <div className="hero__orb hero__orb--2" aria-hidden="true" />
      <ParticleField />

      {/* Live location + clock — top right */}
      {profile.location && (
        <div className="hero__meta">
          <span className="hero__meta-dot" aria-hidden="true" />
          <span>{profile.location}</span>
          {clock && (
            <>
              <span className="hero__meta-sep" aria-hidden="true">·</span>
              <span className="hero__meta-clock" aria-live="polite" aria-atomic="true">
                {clock} <abbr title="East Africa Time" className="hero__meta-tz">EAT</abbr>
              </span>
            </>
          )}
        </div>
      )}

      <div className="container hero__content">

        {/* Availability badge */}
        {profile.available && (
          <div className="hero__status fade-up" style={{ animationDelay: "0.1s" }}>
            <span className="hero__status-dot" />
            <span>{profile.availabilityText || "Available for work"}</span>
          </div>
        )}

        {/* Short intro — context without repeating the name */}
        <p className="hero__intro fade-up" style={{ animationDelay: "0.2s" }}>
          Based in {profile.location || "Nairobi"} — I build things that matter.
        </p>

        {/* Typewriter — cycling roles */}
        <div
          className="hero__role-wrap fade-up"
          style={{ animationDelay: "0.32s" }}
          aria-label={`Role: ${displayed}`}
        >
          <span className="hero__role-text">{displayed}</span>
          <span
            className={`hero__cursor${isDeleting ? " hero__cursor--fast" : ""}`}
            aria-hidden="true"
          />
        </div>

        <div className="hero__divider fade-up" style={{ animationDelay: "0.46s" }} aria-hidden="true" />

        <p className="hero__sub fade-up" style={{ animationDelay: "0.56s" }}>
          {profile.tagline || "Building digital products that bridge technology and people."}
        </p>

        <div className="hero__actions fade-up" style={{ animationDelay: "0.68s" }}>
          <Link to="/projects" className="btn btn-primary">
            See my work <FaArrowRight size={12} />
          </Link>
          <Link to="/contact" className="btn btn-outline">
            Get in Touch
          </Link>
        </div>

        <div className="hero__socials fade-up" style={{ animationDelay: "0.82s" }}>
          {socials
            .filter((s) => s.id !== "whatsapp")
            .map(({ id, label, url, icon }) => {
              const Icon = ICON_MAP[icon];
              return Icon ? (
                <a key={id} href={url} target="_blank" rel="noreferrer"
                   className="hero__social-link" aria-label={label}>
                  <Icon size={16} />
                </a>
              ) : null;
            })}
        </div>

      </div>

      <div className="hero__scroll fade-up" style={{ animationDelay: "1.2s" }}>
        <div className="hero__scroll-wheel" />
        <span className="hero__scroll-label">Scroll</span>
      </div>
    </section>
  );
};

export default Hero;